'use client';

import Image from '@/components/Image';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { afterpayEssentialMap, klarnaEssentialMap } from '@/utils/payment-constants';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { accDiv, floorNumber } from '@/utils/tools';
import { InfoOutlined } from '@zeelool/icon';
import { useContext, useMemo, useState } from 'react';
import CycleTip from './CycleTip';
import { useTranslations } from '@/i18n/use-translations';
import { IS_DE } from '@/utils/constants';

interface CycleMsgProps {
  price: string | number;
  SDProductDetailsPageBtnClick?: (btnName: string) => void;
}

export default function CycleMsg({ price, SDProductDetailsPageBtnClick }: CycleMsgProps) {
  const { currency } = useStore((state) => state);
  const { deviceType } = useContext(ResponsiveContext);
  const [cycleTipShow, setCycleTipShow] = useState(false);
  const t = useTranslations('CycleMsg');

  const isWAP = deviceType === EPlatform.WAP;

  // 计算是否支持afterpay分期
  const afterpayEnable = useMemo(() => {
    return Object.keys(afterpayEssentialMap).includes(currency.name) && Number(price) > 10;
  }, [currency.name, price]);

  // 计算afterpay价格
  const afterpayPrice = useMemo(() => {
    if (afterpayEnable) {
      return floorNumber(accDiv(Number(price), 4));
    }
    return '0.00';
  }, [afterpayEnable, price]);

  // 计算klarna分期期数
  const klarnaCycle = useMemo(() => {
    const info = Object.values(klarnaEssentialMap).find(
      (item) => item.CURRENCY === currency.name && item.CYCLE > 0,
    );
    if (info && Number(price) >= info.MIN && Number(price) <= info.MAX) {
      return info.CYCLE;
    }
    return 0;
  }, [currency.name, price]);

  // 计算klarna价格
  const klarnaPrice = useMemo(() => {
    if (klarnaCycle) {
      return floorNumber(accDiv(Number(price), klarnaCycle));
    }
    return '0.00';
  }, [klarnaCycle, price]);

  // 处理提示图标点击
  const handleTipClick = () => {
    SDProductDetailsPageBtnClick?.('Klarna');
    setCycleTipShow(true);
  };

  // 如果都不支持，不显示组件
  if ((!afterpayEnable && !klarnaCycle) || IS_DE) {
    return null;
  }

  return (
    <div className="cycle-msg">
      <div
        className={`item-staging ${isWAP ? 'text-[11px] text-[#808080] tracking-[0] pt-[15px]' : 'flex items-center justify-center text-[11px] text-[#808080]'}`}
      >
        <span className={isWAP ? 'notranslate leading-[15px]' : 'notranslate px-[3px]'}>
          {t('cycleInstalments', {
            cycle: afterpayEnable ? '4' : klarnaCycle,
            amount: formatPriceNumberWithSymbol(
              afterpayEnable ? afterpayPrice : klarnaPrice,
              currency,
            ),
          })}
        </span>

        {/* Klarna图标 */}
        {klarnaCycle > 0 && (
          <Image
            src="/goods-detail/klarna.png"
            alt="zeelool glass"
            width={58}
            height={isWAP ? 12 : 16}
            className={
              isWAP ? 'w-auto h-[12px] align-middle' : 'h-[16px] w-auto align-middle ml-[6px]'
            }
          />
        )}

        {/* 分隔符 */}
        {afterpayEnable && klarnaCycle > 0 && <span className={isWAP ? 'or' : 'px-[6px]'}>or</span>}

        {/* Afterpay图标 */}
        {afterpayEnable && (
          <Image
            src="/goods-detail/afterpay.png"
            alt="zeelool glass"
            width={58}
            height={isWAP ? 12 : 16}
            className={isWAP ? 'w-auto h-[12px] align-middle' : 'h-[16px] w-auto align-middle'}
          />
        )}

        {/* 提示图标 */}
        <InfoOutlined
          className={
            isWAP
              ? 'text-[13px] text-[#808080] align-middle cursor-pointer'
              : 'text-[12px] align-middle cursor-pointer ml-[4px]'
          }
          onClick={handleTipClick}
        />
      </div>

      {/* 弹窗组件 */}
      <CycleTip
        show={cycleTipShow}
        onClose={() => setCycleTipShow(false)}
        afterpayEnable={afterpayEnable}
        klarnaCycle={klarnaCycle}
      />
    </div>
  );
}
